/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableResponseCard from './SortableResponseCard';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import ResponseCardsTreeNode from '../../../../types/BotBuilder/ResponseCardsTreeNode';
import ResponseCardsTreeNodeItem from '../../../../types/BotBuilder/ResponseCardsTreeNodeItem';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';

export type ResponseCardNodeData = {
  node: BotResponseTreeNode;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  onNodeDelete?: (node: BotResponseTreeNode) => void;
  onNodeAdd?: (
    sourceNode: BotResponseTreeNode,
    targetNode: BotResponseTreeNode
  ) => void;
  highlightedHandle?: string | null;
  isLocked?: boolean;
};

function ResponseCardNode(
  props: NodeProps<ResponseCardNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;
  const { addNode, onNodeDelete, onNodeUpdate } = useContext(BotBuilderContext);

  const { content } = data.node;
  const responseCardContent = content as ResponseCardsTreeNode;
  const [responseCards, setResponseCards] = useState<
    ResponseCardsTreeNodeItem[]
  >(responseCardContent.items || []);
  const responseStepRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (data.node) {
      const { items } = responseCardContent;
      if (items.length === 0) {
        const emptyCard = new ResponseCardsTreeNodeItem('', null, null);
        setResponseCards([emptyCard]);

        setTimeout(() => {
          if (responseStepRefs.current[0]) {
            responseStepRefs.current[0]?.focus();
          }
        }, 100);
      } else {
        setResponseCards(items);

        setTimeout(() => {
          responseStepRefs.current.forEach((textareaRef) => {
            if (textareaRef) {
              const element = textareaRef;
              element.style.height = 'auto';
              element.style.height = `${Math.max(40, element.scrollHeight)}px`;
            }
          });
        }, 0);
      }
    }
  }, [data.node]);

  useDebouncedEffect(
    () => {
      responseCardContent.items = responseCards;
      data.onNodeUpdate(data.node);
    },
    [responseCards],
    300
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    idx: number
  ): void => {
    const updatedItems = responseCards.map((card, i) =>
      i === idx ? { ...card, text: event.target.value } : card
    );

    setResponseCards(updatedItems);
  };

  const handleDelete = (indexToDelete: number): void => {
    if (responseCards.length <= 1) {
      return;
    }

    const connectedNode = responseCardContent.items[indexToDelete]?.nextStep;
    if (connectedNode) {
      addNode(connectedNode);
      responseCardContent.items[indexToDelete].nextStep = null;
    }

    const filteredResponseCards = responseCards.filter(
      (_, idx) => idx !== indexToDelete
    );
    setResponseCards(filteredResponseCards);
    responseCardContent.items = filteredResponseCards;
    onNodeUpdate(data.node);

    setTimeout(() => {
      responseStepRefs.current.forEach((textareaRef) => {
        if (textareaRef) {
          const element = textareaRef;
          element.style.height = 'auto';
          element.style.height = `${Math.max(40, element.scrollHeight)}px`;
        }
      });
    }, 0);
  };

  const handleNodeDelete = async () => {
    if (onNodeDelete) {
      onNodeDelete(data.node);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    card: ResponseCardsTreeNodeItem,
    index: number
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (card.text.trim()) {
        const newCard = new ResponseCardsTreeNodeItem('', null, null);
        const updatedCards = [
          ...responseCards.slice(0, index + 1),
          newCard,
          ...responseCards.slice(index + 1)
        ];
        setResponseCards(updatedCards);

        setTimeout(() => {
          if (responseStepRefs.current[index + 1]) {
            responseStepRefs.current[index + 1]?.focus();
          }
        }, 0);
      }
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor)
  );

  function handleDragEnd(event: any): void {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.toString().split('-').pop(), 10);
      const newIndex = parseInt(over?.id.toString().split('-').pop(), 10);

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedCards = arrayMove(responseCards, oldIndex, newIndex);
        setResponseCards(updatedCards);

        responseCardContent.items = updatedCards;
        onNodeUpdate(data.node);

        setTimeout(() => {
          responseStepRefs.current.forEach((textareaRef) => {
            if (textareaRef) {
              const element = textareaRef;
              element.style.height = 'auto';
              element.style.height = `${Math.max(40, element.scrollHeight)}px`;
            }
          });
        }, 0);
      }
    }
  }

  const sortableCards = responseCards.map((card, index) => {
    const handleId = `${data.node.id}-response-card-handle-${index}`;
    const isHighlighted = data.highlightedHandle === handleId;
    return (
      <SortableResponseCard
        key={handleId}
        id={handleId}
        index={index}
        card={card}
        handleChange={handleChange}
        handleKeyDown={handleKeyDown}
        responseStepRefs={responseStepRefs}
        nodeProps={props}
        handleDelete={handleDelete}
        canDelete={responseCards.length > 1}
        isHighlighted={isHighlighted}
        isLocked={data.isLocked}
      />
    );
  });

  const renderResponseCards = () => {
    return <div className='flex flex-col gap-2 w-full'>{sortableCards}</div>;
  };

  return (
    <BotBuilderNode
      title='Follow up options'
      sourceNode={data.node}
      onNodeDelete={handleNodeDelete}
      deleteButtonRef={deleteButtonRef}
      showAddButton={false}
      isLocked={data.isLocked}
    >
      <div className='flex justify-center gap-2'>
        <div className='bg-[#049A8F] w-10 h-10 flex items-center justify-center rounded-full p-2'>
          <SmartToyIcon className='w-5 h-5' style={{ color: '#002E5F' }} />
        </div>
        <div className='flex flex-col gap-2 w-full'>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              id='sortable'
              items={responseCards.map(
                (_, index) => `${data.node.id}-response-card-handle-${index}`
              )}
              strategy={verticalListSortingStrategy}
            >
              {renderResponseCards()}
            </SortableContext>
          </DndContext>
        </div>
      </div>
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -10 }}
        isConnectable={isConnectable}
      />
    </BotBuilderNode>
  );
}

export default ResponseCardNode;
